import { HttpClient, HttpEvent } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  AuthenticationSelectors,
  AuthenticationService,
  ConfigService,
} from '@orion-platform/core';
import { environment } from '@orion/environment';
import { Helper } from 'apps/orion/src/app/helper/helper';
import { ApiQueryParams } from 'apps/orion/src/app/models/app-config.model';
import { filter, Observable, of, shareReplay, Subject, switchMap, take } from 'rxjs';
import { RequestStatus } from '../models/commit.model';
import { BatchEditPortfolios, PortfolioFilterUIModel } from '../models/portfolio.model';
import { FormSetting } from '../store/carriers-list/carriers-list.types';
import { DataGridResult, PartNumberDto } from '../store/partnumbers-list/partnumbers-list.types';
import { contactTypeList, PORTFOLIO_MODULE_FORM_KEYS } from '../store/portfolio/portfolio.consts';
import {
  ContactType,
  GetContactsFilterDto,
  GetUploadContactsFilterDto,
  PartNumberVendorCodeAdditionInfoDto,
  Portfolio,
  PortfolioContactDto,
  PortfolioInput,
  VendorDto,
} from '../store/portfolio/portfolio.types';
import { VendorsDetails } from '../store/vendors-list/vendors-list.types';
import { UserRoles } from '../types';
import { formatParams, generateHttpParams, getUrl } from '../utils/general.utils';
import { split, splitMap, splitWith } from '../utils/string.utils';
import { DefaultFormSettingsService } from './default-form-settings.service';
import { FileUploadService } from './file-upload.service';
import { ColumnsService } from './columns.service';
import { ColumnsState } from '../types/columns.types';
import { PORTFOLIO_COLUMN_LIST, PORTFOLIO_FIELDS_KEY } from '../pages/portfolio/portfolio.consts';
import { EnumPortfolioFieldStructure, PortfolioListField } from '../models/portfolio.model';
import { Column } from '@orion-platform/datagrid';

@Injectable({
  providedIn: 'root',
})
export class PortfolioService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly config = inject(ConfigService);
  private readonly fileUpload = inject(FileUploadService);
  private readonly authService = inject(AuthenticationService);
  private readonly defaultForms = inject(DefaultFormSettingsService);
  private readonly columns = inject(ColumnsService);

  private readonly mm = environment.modulesBaseUrl.materialManagement;
  private readonly baseUrl = this.config.getSettings('apiBaseUrl') as string;

  // -------------
  // Repeated URLs
  // -------------
  private readonly vendorsUrl = getUrl(this.baseUrl, this.mm.vendors);
  private readonly partNumbersUrl = getUrl(this.baseUrl, this.mm.partNumbers);
  private readonly uploadPortfolioUrl = getUrl(this.baseUrl, this.mm.uploadPortfolio);

  // -------------
  // State/streams
  // -------------
  /**
   * Emits the row count returned by the latest portfolio search. Filter form
   * subscribes to enable Download once results are present and to clear that
   * gate when a fresh search starts (caller emits 0 if the query returned
   * nothing).
   */
  private readonly isSearchVisible = new Subject<number | undefined>();
  private readonly portfolioDetails = new Subject<{
    data?: PortfolioContactDto;
    isRowExpand: boolean;
  }>();
  currentPortfolio?: PortfolioContactDto;
  private vendorWithName?: unknown;

  private readonly upn$ = this.store
    .select(AuthenticationSelectors.upn)
    .pipe(filter(Boolean), shareReplay({ bufferSize: 1, refCount: true }));

  // ------------------------
  // UI helper subjects (same behavior)
  // ------------------------
  getPortfolioDetail(): Observable<{ data?: PortfolioContactDto; isRowExpand: boolean }> {
    return this.portfolioDetails.asObservable();
  }

  setPortfolioDetail(data?: PortfolioContactDto, isRowExpand = true): void {
    this.currentPortfolio = data;
    this.portfolioDetails.next({ data, isRowExpand });
  }

  getPortfolioListReady(): Observable<number | undefined> {
    return this.isSearchVisible.asObservable();
  }

  setPortfolioListReady(rowCount?: number): void {
    this.isSearchVisible.next(rowCount);
  }

  setVendorWithNameList(v: unknown): void {
    this.vendorWithName = v;
  }

  getVendorWithNameList(): unknown {
    return this.vendorWithName;
  }

  async getContactTypeList(): Promise<ContactType[]> {
    const activePlant = this.store.selectSnapshot(AuthenticationSelectors.getActiveplant);
    const groupNames = await this.authService.getGroupNames();

    const exclusions = new Set<string>();

    if (activePlant === 'LS51') {
      exclusions.add('WHBuyer');
    }

    const hasRequiredRole =
      groupNames.includes(UserRoles.Orion_SCM_Analyst) ||
      groupNames.includes(UserRoles.Orion_Administrator);

    if (!hasRequiredRole) {
      exclusions.add('SystemVendor');
    }

    return contactTypeList.filter((c): boolean => !exclusions.has(c.id));
  }

  // ------------------------
  // Routes (actions)
  // ------------------------
  private partNumbersAction(action: string): string {
    return `${this.partNumbersUrl}/${action}`;
  }

  private getApiUrlPortfolioByPlant(action: string): string {
    return `${this.partNumbersUrl}__orchSuffix__/${action}`;
  }

  private vendorsAction(action: string): string {
    return `${this.vendorsUrl}/${action}`;
  }

  // ------------------------
  // CRUD portfolio (Swagger: createportfolio -> PortfolioContactDTO) :contentReference[oaicite:2]{index=2}
  // ------------------------
  createPortfolio(payload: PortfolioInput): Observable<Portfolio> {
    // original used CreatePortfolio action
    return this.http.post<Portfolio>(this.partNumbersAction('CreatePortfolio'), payload);
  }

  updatePortfolio(payload: unknown): Observable<unknown> {
    return this.http.post(this.partNumbersAction('UpdatePortfolio'), payload);
  }

  deletePortfolio(id: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.partNumbersAction('DeletePortfolio')}/${id}`);
  }

  // ------------------------
  // Helpers
  // ------------------------
  private vendorsToUpper(vendorCodes: string[]): string[] {
    if (!vendorCodes?.length) return [];
    return vendorCodes.map((code): string => (code ? Helper.virtualVCReplace(code) : ''));
  }

  // ------------------------
  // Main list: GetContacts (typed + no subscribe)
  // ------------------------
  getContacts(
    filterModel: PortfolioFilterUIModel,
    query: ApiQueryParams,
  ): Observable<DataGridResult<PortfolioContactDto>> {
    return this.upn$.pipe(
      take(1),
      switchMap((email): Observable<DataGridResult<PortfolioContactDto>> => {
        const dto: GetContactsFilterDto = {
          additionField: {
            propertyName: filterModel.additionField,
            propertyValue: filterModel.additionFieldValue,
          },
          contacts: splitWith(filterModel.contacts),
          contactType: filterModel.contactType,
          flags: splitWith(filterModel.flags ? filterModel.flags.toString() : filterModel.flags),
          materialGroups: splitWith(
            filterModel.materialGroups
              ? filterModel.materialGroups.toString()
              : filterModel.materialGroups,
          ),
          partNumbers: splitWith(
            filterModel.partNumbers
              ? filterModel.partNumbers.toString().toUpperCase()
              : filterModel.partNumbers,
          ),
          vendorCodes: this.vendorsToUpper(
            splitWith(
              filterModel.vendorCodes
                ? filterModel.vendorCodes.toString().toUpperCase()
                : filterModel.vendorCodes,
            ),
          ),
        };

        if (filterModel.contact) {
          dto.contacts = [...(dto.contacts ?? []), email];
        }

        return this.http.post<DataGridResult<PortfolioContactDto>>(
          this.getApiUrlPortfolioByPlant('GetContacts'),
          dto,
          { params: formatParams(query) },
        );
      }),
    );
  }

  // ------------------------
  // Download upload contacts -> blob
  // ------------------------
  download(filterModel: PortfolioFilterUIModel, columns: string[]): Observable<Blob> {
    return this.upn$.pipe(
      take(1),
      switchMap((email): Observable<Blob> => {
        const contacts = filterModel.contact
          ? [...split(filterModel.contacts), email]
          : split(filterModel.contacts);

        const dto: GetUploadContactsFilterDto = {
          additionField: {
            propertyName: filterModel.additionField,
            propertyValue: filterModel.additionFieldValue,
          },
          contacts,
          contactType: filterModel.contactType === 'All' ? '' : filterModel.contactType,
          fields: columns,
          flags: split(filterModel.flags),
          materialGroups: split(filterModel.materialGroups),
          partNumbers: splitMap(filterModel.partNumbers, (s: string): string => s.toUpperCase()),
          type: 'XLSX',
          vendorCodes: this.vendorsToUpper(
            splitMap(filterModel.vendorCodes, (s: string): string => s.toUpperCase()),
          ),
        };

        if (filterModel.sorts) {
          dto.sorts = filterModel.sorts;
        }

        return this.http.post(this.getApiUrlPortfolioByPlant('getuploadcontacts'), dto, {
          responseType: 'blob',
        });
      }),
    );
  }

  // ------------------------
  // Upload portfolio
  // ------------------------
  uploadPortfolio(fileName: string, file: File): Observable<HttpEvent<RequestStatus>> {
    return this.fileUpload.uploadFormData(this.uploadPortfolioUrl, fileName, file);
  }

  // ------------------------
  // Addition info (PN+VC)
  // ------------------------
  getAdditionInfoPropertyNames(): Observable<string[]> {
    return this.http.get<string[]>(
      this.getApiUrlPortfolioByPlant('GetPartNumberVendorCodeAdditionInfoPropertyNames'),
    );
  }

  getAdditionInfoByPnVc(payload: VendorDto[]): Observable<PartNumberVendorCodeAdditionInfoDto[]> {
    return this.http.post<PartNumberVendorCodeAdditionInfoDto[]>(
      this.partNumbersAction('getpartnumbervendorcodeadditioninfos'),
      payload,
    );
  }

  createOrUpdateAdditionInfoByPnVc(
    payload: PartNumberVendorCodeAdditionInfoDto,
  ): Observable<PartNumberVendorCodeAdditionInfoDto> {
    return this.http.post<PartNumberVendorCodeAdditionInfoDto>(
      this.partNumbersAction('updatepartnumbervendorcodeadditioninfo'),
      payload,
    );
  }

  deleteAdditionInfo(propertyName: string): Observable<PartNumberVendorCodeAdditionInfoDto> {
    return this.http.delete<PartNumberVendorCodeAdditionInfoDto>(
      this.partNumbersAction(`deletepartnumbervendorcodeadditioninfo/${propertyName}`),
    );
  }

  // ------------------------
  // Batch edit
  // ------------------------
  batchEditContacts(payload: BatchEditPortfolios): Observable<RequestStatus> {
    return this.http.post<RequestStatus>(this.partNumbersAction('BatchEditContacts'), payload);
  }

  batchEditContactsRequestStatus(requestHash: string): Observable<RequestStatus> {
    return this.http.get<RequestStatus>(this.partNumbersAction(`-/RequestStatus/${requestHash}`));
  }

  // ------------------------
  // Info endpoints
  // ------------------------
  getPartNumberInfo(partNumber: string): Observable<PartNumberDto> {
    const params = generateHttpParams({ partNumber });
    return this.http.get<PartNumberDto>(this.partNumbersAction('GetPartNumberInfo'), { params });
  }

  getVendorCodeInfo(vendorCode: string): Observable<VendorsDetails> {
    const params = generateHttpParams({ vendorCode });
    return this.http.get<VendorsDetails>(this.vendorsAction('GetVendorInfo'), { params });
  }

  // -----------------------
  // Default form settings
  // -----------------------

  /**
   * The portfolio columns. Open schema: the grid also grows per-plant addition
   * fields that arrive with the data, so a stored column the structure does not
   * list is kept rather than dropped.
   * @returns {Observable<ColumnsState>} The columns, and whether they are still the defaults.
   */
  getDefaultColumns(): Observable<ColumnsState> {
    return this.columns.load({
      buildDefaults: (formFields: string[]): Column[] =>
        new PortfolioListField(EnumPortfolioFieldStructure.PortfolioTable, formFields)
          .fields as unknown as Column[],
      catalogue: (): Column[] =>
        new PortfolioListField(EnumPortfolioFieldStructure.PortfolioTable)
          .fields as unknown as Column[],
      columnListKey: PORTFOLIO_COLUMN_LIST,
      formFields$: this.getDefaultFormSettingFields(PORTFOLIO_FIELDS_KEY),
    });
  }

  getDefaultFormSettingFields(formKey: string): Observable<string[]> {
    return this.defaultForms.getFields(`${this.partNumbersUrl}__orchSuffix__`, formKey);
  }

  getDefaultFormSettingFormKeys(): Observable<string[]> {
    return of(PORTFOLIO_MODULE_FORM_KEYS);
  }

  getDefaultFormSettings(formKey: string): Observable<FormSetting[]> {
    return this.defaultForms.getFormSettings(this.partNumbersUrl, formKey);
  }

  updateDefaultFormSettings(formKey: string, payload: FormSetting[]): Observable<FormSetting[]> {
    return this.defaultForms.update(this.partNumbersUrl, formKey, payload);
  }

  /**
   * Which fields the create form shows, as the user arranged them.
   *
   * The same list in the same shape as a grid's columns, stored the same way —
   * only what draws it differs.
   * @returns {Observable<ColumnsState>} The fields, and whether they are still the defaults.
   */
  getFormFieldColumns = (): Observable<ColumnsState> =>
    this.columns.load({
      buildDefaults: (formFields: string[]): Column[] =>
        new PortfolioListField(EnumPortfolioFieldStructure.PortfolioCreateForm, formFields)
          .fields as unknown as Column[],
      columnListKey: 'portfolio_field_list',
      formFields$: this.getDefaultFormSettingFields('PORTFOLIO_DETAIL_FORMKEY'),
    });
}
